import { type AppSupabaseClient } from '@/platform/supabase/client'
import { ERROR_CODE } from '@/shared/errors/codes'
import { AppError } from '@/shared/errors/envelope'

/**
 * Parent authentication (FR-01): Supabase Auth magic link.
 *
 * Google sign-in is deferred until an OAuth client is configured; anonymous
 * sign-in is never used for parents (it remains KT1-only).
 *
 * Invite-only outside a local stack: `allowSignUp: false` sends
 * `shouldCreateUser: false`, so an email without an account (an operator
 * invites parents from the Supabase dashboard) gets `ACCESS_DENIED` instead of
 * a new account. The hosted Auth setting "Allow new users to sign up" = off is
 * the authoritative control; this is an additional client-side guard.
 */

export interface AuthOptions {
  /** Create an account for an unknown email (local Supabase stack only). */
  readonly allowSignUp: boolean
}

/** GoTrue error codes for "no account, and sign-up is not allowed". */
const NOT_INVITED_CODES: ReadonlySet<string> = new Set([
  'otp_disabled',
  'signup_disabled',
])

export interface SessionUser {
  readonly id: string
  readonly email: string | null
}

export interface AuthApi {
  getSessionUser(): Promise<SessionUser | null>
  /** Calls back on sign-in, sign-out and token refresh. Returns unsubscribe. */
  onSessionChange(callback: (user: SessionUser | null) => void): () => void
  sendMagicLink(email: string): Promise<void>
  signOut(): Promise<void>
}

export function createAuthApi(
  client: AppSupabaseClient,
  redirectTo: string,
  { allowSignUp }: AuthOptions = { allowSignUp: false },
): AuthApi {
  const toUser = (user: { id: string; email?: string | undefined } | undefined | null) =>
    user ? { id: user.id, email: user.email ?? null } : null

  return {
    getSessionUser: async () => {
      const { data } = await client.auth.getSession()
      return toUser(data.session?.user)
    },
    onSessionChange: (callback) => {
      const { data } = client.auth.onAuthStateChange((_event, session) => {
        callback(toUser(session?.user))
      })
      return () => {
        data.subscription.unsubscribe()
      }
    },
    sendMagicLink: async (email) => {
      const { error } = await client.auth.signInWithOtp({
        email,
        options: { emailRedirectTo: redirectTo, shouldCreateUser: allowSignUp },
      })
      if (error !== null) {
        const code =
          error.status === 429
            ? ERROR_CODE.RATE_LIMITED
            : NOT_INVITED_CODES.has(error.code ?? '')
              ? ERROR_CODE.ACCESS_DENIED
              : ERROR_CODE.INTERNAL_ERROR
        throw new AppError(code, { cause: error })
      }
    },
    signOut: async () => {
      await client.auth.signOut()
    },
  }
}
